// src/pages/Folder/AllFolders.tsx

import {
  AccountBalanceTwoTone,
  AddTwoTone,
  AppsTwoTone,
  EditTwoTone,
  HighlightOffTwoTone,
  InfoTwoTone,
  ListTwoTone as ListIconTwoTone,
  MapTwoTone,
  MoreVertTwoTone,
  SearchTwoTone,
  VerifiedUserTwoTone,
} from '@mui/icons-material'
import {
  Alert,
  Avatar,
  Box,
  IconButton,
  ImageList,
  ImageListItem,
  ImageListItemBar,
  List,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { FolderPool } from '@/types/domain/folder'

import { ASC, DESC, getStorage, isFilterMatch, setStorage } from '../../api'
import {
  AM,
  AMItem,
  DlgFolderMap,
  DlgFolderPoolInfo,
  DlgIntegrityCheck,
  FolderList,
  FolderSortSelector,
  HStack,
  KPage,
  KTextField,
} from '../../components'
import { languageConstants as LC } from '../../constants'
import { useFolderPool } from '../../hooks'
import useRenderCount from '../../hooks/tools/useRenderCount'

type Props = {
  PAGE_CONFIG?: string
}

export const AllFolders = ({ PAGE_CONFIG = 'settings_folders_all' }: Props) => {
  // render performance
  const rc = useRenderCount()
  console.log('AllFolders', rc)

  const navigate = useNavigate()
  const fPool: FolderPool = useFolderPool()

  const [config, setConfig] = useState(getStorage(PAGE_CONFIG))
  const [actionMenu, setActionMenu] = useState(null)

  const inputFilter = f =>
    config.filter
      ? isFilterMatch(config.filter, f.attributes.name) ||
        isFilterMatch(config.filter, f.attributes.street) ||
        isFilterMatch(config.filter, f.attributes.zip) ||
        isFilterMatch(config.filter, f.attributes.city) ||
        Number(config.filter) === Number(f.id)
      : f

  const typeFilter = f =>
    config.type === LC.FOLDERTYPE_DEFAULT
      ? f.attributes.parent_id === null
      : config.type === LC.FOLDERTYPE_SUB
        ? f.attributes.parent_id !== null
        : f

  const sortFilter = (a, b) => {
    const { order, orderby } = config

    if (
      orderby === 'name' &&
      a.attributes[orderby].toLowerCase() < b.attributes[orderby].toLowerCase()
    )
      return order === DESC ? 1 : -1

    if (orderby === 'entity_id' && Number(a.id) < Number(b.id))
      return order === DESC ? 1 : -1

    return order === DESC ? -1 : 1
  }

  useEffect(() => setStorage(PAGE_CONFIG, config), [config, PAGE_CONFIG])

  const actionButton = (
    <IconButton onClick={event => setActionMenu(event.currentTarget)}>
      <MoreVertTwoTone />
    </IconButton>
  )

  const avatar = (
    <Avatar>
      <AccountBalanceTwoTone />
    </Avatar>
  )

  const changeOrderByFilter = (event, orderby) => {
    const { order } = config
    if (orderby !== null) {
      setConfig(prev => ({ ...prev, orderby }))
    } else {
      const newOrder = order === DESC ? ASC : DESC
      setConfig(prev => ({ ...prev, order: newOrder }))
    }
  }

  return (
    <>
      {/* map */}
      <DlgFolderMap
        onClose={() => setConfig(prev => ({ ...prev, map: false }))}
        open={config.map || false}
      />
      {/* map end */}

      {/* integrity check */}
      <DlgIntegrityCheck
        onClose={() => setConfig(prev => ({ ...prev, integrityCheck: false }))}
        open={config.integrityCheck || false}
      />
      {/* integrity check end */}

      {/* action menu */}
      <AM
        anchorEl={actionMenu}
        caption={'Gebäude'}
        onClose={() => setActionMenu(null)}
        open={Boolean(actionMenu)}
        historyUrlTarget={'dashboard'}
      >
        <AMItem
          caption={'Neu'}
          icon={<AddTwoTone />}
          onClick={() => navigate('/folder/new')}
        />

        <AMItem
          caption={'Info'}
          icon={<InfoTwoTone />}
          onClick={() => setConfig(prev => ({ ...prev, showStats: true }))}
        />
      </AM>
      {/* action menu end */}

      {/* pool stat */}
      <DlgFolderPoolInfo
        open={config.showStats || false}
        onClose={() => setConfig(prev => ({ ...prev, showStats: false }))}
      />
      {/* pool stat end */}

      {fPool.length > 0 && (
        <KPage action={actionButton} avatar={avatar} title={`Gebäude`}>
          <HStack justifyContent={'space-between'} sx={{ mx: 1, mb: 1 }}>
            <HStack>
              <Stack
                alignItems={'center'}
                direction={{ lg: 'column', xl: 'row' }}
                spacing={1}
              >
                <Typography>Typ</Typography>
                <ToggleButtonGroup
                  exclusive
                  onChange={(event, type) => {
                    if (type !== null) setConfig(prev => ({ ...prev, type }))
                  }}
                  value={config.type}
                >
                  {[
                    { label: 'Alle', value: LC.FOLDERTYPE_ANY },
                    {
                      label: LC.FOLDERTYPE_DEFAULT,
                      value: LC.FOLDERTYPE_DEFAULT,
                    },
                    { label: LC.FOLDERTYPE_SUB, value: LC.FOLDERTYPE_SUB },
                  ].map(item => (
                    <ToggleButton key={item.value} value={item.value}>
                      {item.label}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </Stack>

              <Stack
                alignItems={'center'}
                direction={{ lg: 'column', xl: 'row' }}
                spacing={1}
              >
                <Typography>Ansicht</Typography>
                <ToggleButtonGroup
                  exclusive
                  onChange={(event, itemView) => {
                    if (itemView !== null)
                      setConfig(prev => ({ ...prev, itemView }))
                  }}
                  value={config.itemView}
                >
                  {[
                    { icon: <ListIconTwoTone />, value: 'list' },
                    { icon: <AppsTwoTone />, value: 'tile' },
                  ].map(item => (
                    <ToggleButton key={item.value} value={item.value}>
                      {item.icon}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </Stack>

              <Stack
                alignItems={'center'}
                direction={{ lg: 'column', xl: 'row' }}
                spacing={1}
              >
                <Typography>Tools</Typography>
                <ToggleButtonGroup
                  onChange={(event, target) => {
                    if (target[0] === 'map')
                      setConfig(prev => ({ ...prev, map: true }))
                    if (target[0] === 'integrity')
                      setConfig(prev => ({ ...prev, integrityCheck: true }))
                  }}
                >
                  <Tooltip title={'Kartenübersicht Standorte'}>
                    <ToggleButton value={'map'}>
                      <MapTwoTone />
                    </ToggleButton>
                  </Tooltip>
                  <Tooltip title={'Integritätsprüfung'}>
                    <ToggleButton value={'integrity'}>
                      <VerifiedUserTwoTone />
                    </ToggleButton>
                  </Tooltip>
                </ToggleButtonGroup>
              </Stack>
            </HStack>

            <FolderSortSelector
              onChange={changeOrderByFilter}
              order={config.order}
              orderby={config.orderby}
            />
          </HStack>

          {/* filter */}
          <KTextField
            icon={<SearchTwoTone />}
            onChange={event => {
              const filter = event.target.value

              setConfig(prev => ({ ...prev, filter }))
            }}
            unit={
              config.filter && (
                <Tooltip title={'Filter löschen'}>
                  <IconButton
                    onClick={() => setConfig(prev => ({ ...prev, filter: '' }))}
                  >
                    <HighlightOffTwoTone />
                  </IconButton>
                </Tooltip>
              )
            }
            value={config.filter}
          />

          {/* hit hint */}
          <Alert severity={'info'} sx={{ mx: 1 }}>
            <HStack>
              <Typography>
                <strong>
                  {fPool.filter(inputFilter).filter(typeFilter).length}
                </strong>{' '}
                Gebäude entsprechen dem Suchfilter{' '}
                <strong>
                  &quot;
                  {config.filter}
                  &quot;
                </strong>
              </Typography>
            </HStack>
          </Alert>

          {/* list view */}
          {config.itemView === 'list' && (
            <List
              sx={{
                m: 1,
                maxHeight: 200,
                minHeight: 'calc(100vh - 560px)',
                overflow: 'auto',
              }}
            >
              {fPool
                .filter(inputFilter)
                .filter(typeFilter)
                .sort(sortFilter)
                .map(f => (
                  <FolderList id={Number(f.id)} key={f.id} />
                ))}
            </List>
          )}

          {/* tile view */}
          {config.itemView === 'tile' && (
            <ImageList
              sx={{
                m: 1,
                maxHeight: 200,
                minHeight: 'calc(100vh - 560px)',
                overflow: 'auto',
              }}
              cols={6}
            >
              {fPool.filter(inputFilter).map(item => {
                const { image_url, name, name_short } = item.attributes

                return (
                  <ImageListItem cols={2} key={name} sx={{ cursor: 'pointer' }}>
                    <Box
                      alt={''}
                      component={'img'}
                      onClick={() => navigate(`/folder/${item.id}`)}
                      src={image_url}
                    />

                    <ImageListItemBar
                      actionIcon={
                        <IconButton
                          onClick={() => navigate(`/folder/${item.id}/edit`)}
                        >
                          <EditTwoTone />
                        </IconButton>
                      }
                      subtitle={name_short}
                      title={name}
                    />
                  </ImageListItem>
                )
              })}
            </ImageList>
          )}
        </KPage>
      )}
    </>
  )
}
